import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { runFreeScan, runAiSearch } from "@/api/discovery";
import { createPipeline } from "@/api/pipeline";
import type { ExploreResult } from "@/types";
import { cn } from "@/cn";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import {
  useDiscoveryProfile,
  useAddDiscoveredOffer,
  useScanHistory,
  enabledCompanyCount,
  enabledProviderCount,
} from "@/hooks/useDiscovery";
import { DiscoveryProfileEditor } from "@/components/discovery/discovery-profile-editor";
import {
  parseLeadEnvelopes,
  stripLeadEnvelopes,
} from "@/lib/lead-envelope";
import { IntakePanels } from "@/components/discovery/intake-panels";

interface ScanMode {
  id: "free" | "ai";
  label: string;
  description: string;
}

type DiscoveryTab = "signal" | "ai" | "import";

const DISCOVERY_TABS: { id: DiscoveryTab; label: string }[] = [
  { id: "signal", label: "Signal Scan" },
  { id: "ai", label: "AI Search" },
  { id: "import", label: "Import" },
];

const SCAN_MODES: ScanMode[] = [
  {
    id: "free",
    label: "Signal Scan (ATS + Boards)",
    description:
      "Zero-token scan of tracked companies for hiring and growth signals.",
  },
  {
    id: "ai",
    label: "AI Lead Discovery",
    description:
      "Natural language B2B lead discovery using your narrative and ICP.",
  },
];

const SENIORITY_LEVELS = [
  "Intern",
  "Entry",
  "Mid",
  "Senior",
  "Lead",
  "Manager",
  "Director",
  "VP",
  "C-Level",
];

function resultKey(r: ExploreResult, i: number): string {
  return r.id ?? `${r.url}-${i}`;
}

function csvToList(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function JobDiscoveryPage({ embedded = false }: { embedded?: boolean } = {}) {
  // embedded: when rendered under Lists → Discover jobs
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: profile } = useDiscoveryProfile();
  const { data: historyData } = useScanHistory();
  const scanHistory = historyData?.history ?? [];
  const addOfferMutation = useAddDiscoveredOffer();

  const [discoveryTab, setDiscoveryTab] = useState<DiscoveryTab>("signal");
  const [mode, setMode] = useState<ScanMode["id"]>("free");
  const [results, setResults] = useState<ExploreResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [aiReasoning, setAiReasoning] = useState("");
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const resultsPagination = useClientPagination(results);
  const historyPagination = useClientPagination(scanHistory);

  const [keywords, setKeywords] = useState("");
  const [location, setLocation] = useState("");
  const [seniority, setSeniority] = useState<string[]>([]);

  const [aiQuery, setAiQuery] = useState("");
  const aiQueryInputRef = useRef<HTMLInputElement>(null);
  const urlParamsAppliedRef = useRef(false);
  const inboxRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const aiTextBufferRef = useRef("");
  const seenOfferUrlsRef = useRef(new Set<string>());

  const enabledCount = enabledCompanyCount(profile);
  const providerCount = enabledProviderCount(profile);
  const hasScanSources = enabledCount > 0 || providerCount > 0;

  useEffect(() => {
    if (urlParamsAppliedRef.current) return;

    const companyParam = searchParams.get("company")?.trim();
    const modeParam = searchParams.get("mode");

    if (companyParam) {
      urlParamsAppliedRef.current = true;
      const resolvedMode = modeParam === "free" ? "free" : "ai";
      setMode(resolvedMode);

      if (resolvedMode === "ai") {
        setAiQuery(`Open jobs at ${companyParam}`);
        requestAnimationFrame(() => aiQueryInputRef.current?.focus());
      } else {
        setKeywords(companyParam);
      }
      return;
    }

    if (modeParam === "ai" || modeParam === "free") {
      urlParamsAppliedRef.current = true;
      setMode(modeParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!profile || urlParamsAppliedRef.current) return;
    if (!keywords && profile.signalFilter?.positive?.length) {
      setKeywords(profile.signalFilter.positive.join(", "));
    }
    if (!location && profile.locationFilter?.allow?.length) {
      setLocation(profile.locationFilter.allow.join(", "));
    }
  }, [profile, keywords, location]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  function toggleSeniority(level: string) {
    setSeniority((prev) =>
      prev.includes(level)
        ? prev.filter((l) => l !== level)
        : [...prev, level],
    );
  }

  function buildScanFilters() {
    const titlePositive = csvToList(keywords);
    const locationAllow = csvToList(location);
    const seniorityBoost: Record<string, number> = {};
    for (const level of seniority) {
      seniorityBoost[level.toLowerCase()] = 5;
    }
    return {
      titlePositive: titlePositive.length ? titlePositive : undefined,
      locationAllow: locationAllow.length ? locationAllow : undefined,
      seniorityBoost: Object.keys(seniorityBoost).length
        ? seniorityBoost
        : undefined,
    };
  }

  async function handleFreeScan() {
    if (!hasScanSources) {
      setShowProfileEditor(true);
      setError(
        "Add at least one tracked company or enable a board provider before scanning.",
      );
      return;
    }

    setError(null);
    setActionMessage(null);
    setLoading(true);
    setProgress("Querying portals…");
    setResults([]);
    setAiReasoning("");

    try {
      const res = await runFreeScan(buildScanFilters());
      setResults(res.results);
      setProgress(
        res.results.length > 0
          ? `Found ${res.results.length} new result(s)`
          : "Scan complete — no new matches (results may already be in history).",
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "Scan failed. Please try again.";
      setError(msg);
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }

  async function handleAiSearch() {
    if (!aiQuery.trim()) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setError(null);
    setActionMessage(null);
    setLoading(true);
    setProgress("Searching with AI…");
    setResults([]);
    setAiReasoning("");
    aiTextBufferRef.current = "";
    seenOfferUrlsRef.current = new Set();

    try {
      await runAiSearch(
        aiQuery.trim(),
        (event) => {
          if (event.type === "text") {
            aiTextBufferRef.current += event.text;
            const offers = parseLeadEnvelopes(aiTextBufferRef.current);
            const newResults: ExploreResult[] = [];

            for (let i = 0; i < offers.length; i++) {
              const offer = offers[i];
              const url = offer.url ?? `${offer.company}-${i}`;
              if (seenOfferUrlsRef.current.has(url)) continue;
              seenOfferUrlsRef.current.add(url);
              newResults.push({
                portal: offer.source ?? "ai-prospect",
                url,
                title: offer.dealName ?? offer.title ?? offer.company,
                company: offer.company,
                location: offer.location,
                postedAt: offer.postedHint,
                why: offer.why ?? offer.signal,
                source: offer.source ?? "ai-prospect",
                verification: offer.verification,
                id: url,
              });
            }

            if (newResults.length > 0) {
              setResults((prev) => [...prev, ...newResults]);
            }
            setAiReasoning(stripLeadEnvelopes(aiTextBufferRef.current));
          } else if (event.type === "status") {
            setProgress(event.label);
          } else if (event.type === "error") {
            setError(event.msg);
          } else if (event.type === "done") {
            setProgress(
              `Search complete${event.tokens ? ` (${event.tokens} tokens)` : ""}`,
            );
          }
        },
        controller.signal,
      );
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") return;
      const msg =
        err instanceof Error ? err.message : "Search failed. Please try again.";
      setError(msg);
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }

  function handleCancelAi() {
    abortRef.current?.abort();
    setLoading(false);
    setProgress("Search cancelled.");
  }

  async function handleAddToInbox(result: ExploreResult, index: number) {
    setActionMessage(null);
    try {
      const source =
        result.source === "ai-prospect" || result.source === "ai-search" || result.portal === "ai-search"
          ? "ai-prospect"
          : "signal-scan";

      const res = await addOfferMutation.mutateAsync({
        offers: [
          {
            url: result.url,
            company: result.company,
            dealName: result.title,
            role: result.title,
            location: result.location,
            compensation: result.compensation,
            source,
          },
        ],
        source,
      });

      const job = res.added[0];
      if (job) {
        setResults((prev) =>
          prev.map((r, i) =>
            i === index ? { ...r, addedOpportunityId: job.id } : r,
          ),
        );
        setActionMessage(
          `Added ${result.title} at ${result.company} as a lead.`,
        );
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to add to inbox.",
      );
    }
  }

  async function handleStartPipeline(opportunityId: string) {
    try {
      const res = await createPipeline(opportunityId);
      const pipelineId = res.pipeline?.id ?? res.pipeline?.pipelineId;
      if (pipelineId) {
        navigate(`/sequences/${pipelineId}`);
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to create pipeline.",
      );
    }
  }

  return (
    <>
      {!embedded && (
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Discover jobs</h1>
          <p className="text-muted-foreground">
            Signal scan, AI job search, and lead import. For Apollo people search, use{" "}
            <Link to="/people" className="text-brand hover:underline">
              People
            </Link>
            .
          </p>
        </header>
      )}

      <nav className="mb-6 flex gap-1 overflow-x-auto border-b border-border pb-px">
        {DISCOVERY_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setDiscoveryTab(tab.id);
              if (tab.id === "signal") setMode("free");
              if (tab.id === "ai") setMode("ai");
            }}
            className={cn(
              "shrink-0 px-4 py-2.5 text-sm font-medium transition",
              discoveryTab === tab.id
                ? "border-b-2 border-brand text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {discoveryTab === "import" ? (
        <IntakePanels />
      ) : (
        <>

      {/* Sources summary */}
      <div className="mb-6 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-foreground">
              Your sources ({enabledCount} {enabledCount === 1 ? "company" : "companies"}
              {providerCount > 0
                ? ` · ${providerCount} ${providerCount === 1 ? "provider" : "providers"}`
                : ""}
              )
            </p>
            <p className="text-xs text-muted-foreground">
              Free Scan checks your tracked ATS companies and enabled board
              aggregators.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowProfileEditor((v) => !v)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/20"
            >
              {showProfileEditor ? "Hide setup" : "Configure companies"}
            </button>
            <Link
              to="/lists?tab=providers"
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand/5"
            >
              Manage intake sources
            </Link>
            <Link
              to="/settings/signals"
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/20"
            >
              Provider catalog
            </Link>
          </div>
        </div>
        {showProfileEditor && (
          <div className="mt-4 border-t border-border pt-4">
            <DiscoveryProfileEditor compact onSaved={() => setShowProfileEditor(false)} />
          </div>
        )}
        {!hasScanSources && !showProfileEditor && (
          <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
            No scan sources configured — add tracked companies or enable board
            providers on{" "}
            <Link to="/lists?tab=providers" className="underline hover:text-amber-500">
              Lists → Providers
            </Link>
            .
          </p>
        )}
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        {discoveryTab === "signal" &&
          SCAN_MODES.filter((m) => m.id === "free").map((m) => (
            <div
              key={m.id}
              className="rounded-lg border border-brand bg-brand/5 p-4 text-left"
            >
              <h3 className="font-medium text-foreground">{m.label}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>
            </div>
          ))}
        {discoveryTab === "ai" &&
          SCAN_MODES.filter((m) => m.id === "ai").map((m) => (
            <div
              key={m.id}
              className="rounded-lg border border-brand bg-brand/5 p-4 text-left"
            >
              <h3 className="font-medium text-foreground">{m.label}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>
            </div>
          ))}
      </div>

      {discoveryTab === "signal" && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Filters
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="keywords" className="text-xs font-medium text-foreground">
                Keywords
              </label>
              <input
                id="keywords"
                type="text"
                placeholder="e.g. AI Engineer, Product Manager"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="location" className="text-xs font-medium text-foreground">
                Location
              </label>
              <input
                id="location"
                type="text"
                placeholder="e.g. Remote, New York, London"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">Seniority</label>
              <div className="flex flex-wrap gap-1">
                {SENIORITY_LEVELS.map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => toggleSeniority(level)}
                    className={cn(
                      "rounded-md border px-2 py-1 text-[11px] transition",
                      seniority.includes(level)
                        ? "border-brand bg-brand/10 text-brand"
                        : "border-border text-muted-foreground hover:border-muted",
                    )}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {discoveryTab === "ai" && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <label htmlFor="ai-query" className="mb-2 block text-sm font-medium text-foreground">
            Describe what you&apos;re looking for
          </label>
          <div className="flex gap-2">
            <input
              ref={aiQueryInputRef}
              id="ai-query"
              type="text"
              placeholder='e.g. "Senior backend role at a Series B startup focused on ML infrastructure"'
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAiSearch();
              }}
              className="flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <button
              type="button"
              onClick={handleAiSearch}
              disabled={loading || !aiQuery.trim()}
              className="rounded-md bg-brand px-5 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Searching…" : "Search"}
            </button>
            {loading && (
              <button
                type="button"
                onClick={handleCancelAi}
                className="rounded-md border border-border px-4 py-2 text-sm text-muted-foreground"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {mode === "free" && (
        <button
          type="button"
          onClick={handleFreeScan}
          disabled={loading}
          className="mb-6 inline-flex items-center gap-2 rounded-md bg-brand px-6 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              Scanning portals…
            </>
          ) : (
            "Start Scan"
          )}
        </button>
      )}

      {progress && (
        <div className="mb-6 flex items-center gap-2 rounded-md border border-brand/20 bg-brand/5 px-4 py-2 text-sm text-brand">
          {loading && (
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-brand/30 border-t-brand" />
          )}
          {progress}
        </div>
      )}

      {aiReasoning && discoveryTab === "ai" && (
        <div className="mb-6 rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground whitespace-pre-wrap max-h-48 overflow-y-auto">
          {aiReasoning}
        </div>
      )}

      {actionMessage && (
        <div className="mb-6 rounded-md border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
          {actionMessage}
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {results.length > 0 && (
        <div ref={inboxRef}>
          <h2 className="mb-3 text-lg font-semibold text-foreground">
            Results
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({results.length} found)
            </span>
          </h2>
          <div className="grid gap-3">
            {resultsPagination.pageItems.map((r, i) => (
              <div
                key={resultKey(r, i)}
                className="rounded-lg border border-border bg-card p-4 transition hover:border-muted"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-brand">
                        {r.portal}
                      </span>
                      <span className="text-xs text-muted-foreground">|</span>
                      <span className="text-xs text-muted-foreground">{r.company}</span>
                      {r.verification && (
                        <>
                          <span className="text-xs text-muted-foreground">|</span>
                          <span className="text-xs text-amber-600">{r.verification}</span>
                        </>
                      )}
                      {r.postedAt && (
                        <>
                          <span className="text-xs text-muted-foreground">|</span>
                          <span className="text-xs text-muted-foreground">{r.postedAt}</span>
                        </>
                      )}
                    </div>
                    <h3 className="font-medium text-foreground">{r.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {r.location ?? "Location not specified"}
                      {r.compensation && (
                        <>
                          {" "}
                          &middot;{" "}
                          <span className="text-success">{r.compensation}</span>
                        </>
                      )}
                    </p>
                    {r.why && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {r.why}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-md border border-border px-3 py-1.5 text-center text-xs font-medium text-muted-foreground transition hover:bg-muted/20"
                    >
                      View
                    </a>
                    {r.addedOpportunityId ? (
                      <>
                        <Link
                          to={`/deals/${r.addedOpportunityId}`}
                          className="rounded-md border border-brand/30 bg-brand/10 px-3 py-1.5 text-center text-xs font-medium text-brand"
                        >
                          In inbox
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleStartPipeline(r.addedOpportunityId!)}
                          className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground"
                        >
                          Pipeline
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAddToInbox(r, i)}
                        disabled={addOfferMutation.isPending}
                        className="rounded-md bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand transition hover:bg-brand/20 disabled:opacity-50"
                      >
                        + Inbox
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <PaginationBar
            page={resultsPagination.page}
            pageSize={resultsPagination.pageSize}
            totalItems={results.length}
            totalPages={resultsPagination.totalPages}
            showingFrom={resultsPagination.showingFrom}
            showingTo={resultsPagination.showingTo}
            onPageChange={resultsPagination.setPage}
            onPageSizeChange={resultsPagination.setPageSize}
          />
        </div>
      )}

      {scanHistory.length > 0 && (
        <div className="mt-10">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Recent scan history
          </h2>
          <div className="grid gap-2">
            {historyPagination.pageItems.map((entry) => (
              <div
                key={entry.SK ?? entry.url}
                className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-xs"
              >
                <div className="min-w-0 truncate">
                  <span className="font-medium text-foreground">
                    {entry.title ?? "Unknown"}
                  </span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {entry.company} · {entry.portal}
                  </span>
                </div>
                {entry.url && (
                  <a
                    href={entry.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 shrink-0 text-brand hover:underline"
                  >
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
          <PaginationBar
            page={historyPagination.page}
            pageSize={historyPagination.pageSize}
            totalItems={scanHistory.length}
            totalPages={historyPagination.totalPages}
            showingFrom={historyPagination.showingFrom}
            showingTo={historyPagination.showingTo}
            onPageChange={historyPagination.setPage}
            onPageSizeChange={historyPagination.setPageSize}
          />
        </div>
      )}

      {!loading && results.length === 0 && !error && !progress && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {discoveryTab === "signal"
              ? 'Click "Start Scan" to discover growth signals from tracked companies.'
              : "Enter a search query above to find B2B leads with AI."}
          </p>
        </div>
      )}
        </>
      )}
    </>
  );
}
